/**
 * Skin-weight heat colouring: influence 0..1 → an RGB ramp, black at zero, red at full.
 *
 * Pure array maths so the mapping can be asserted in tests without a GPU. The output layout is
 * three floats per vertex, ready to hand to a `BufferAttribute` named `color`.
 */

/** Weights below this read as "no influence" and stay pure black. */
const EPS = 1e-6;

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * One weight → `[r, g, b]`.
 *
 * The ramp is the black→red one the brief asks for, with a small twist that costs nothing and
 * saves a lot of squinting: above 0.75 a little green is mixed in, so "fully owned by this bone"
 * (orange) is distinguishable from "mostly owned" (red). Zero stays exactly black so unweighted
 * regions are unmistakable.
 */
export function heatColor(weight: number): [number, number, number] {
  const w = clamp01(weight);
  if (w <= EPS) return [0, 0, 0];
  const g = w <= 0.75 ? 0 : (w - 0.75) / 0.25 * 0.55;
  return [w, g, 0];
}

/**
 * Whole-mask version. Writes into `out` when it is the right size, otherwise allocates —
 * repainting a mesh every stroke should not churn a megabyte of Float32Array each time.
 */
export function heatColorsForMask(mask: Float32Array, out?: Float32Array): Float32Array {
  const target = out && out.length === mask.length * 3 ? out : new Float32Array(mask.length * 3);
  for (let i = 0; i < mask.length; i++) {
    const [r, g, b] = heatColor(mask[i]);
    target[i * 3] = r;
    target[i * 3 + 1] = g;
    target[i * 3 + 2] = b;
  }
  return target;
}

/** Flat grey for "skin exists but no bone is selected", same layout as `heatColorsForMask`. */
export function flatColors(vertexCount: number, level = 0.35, out?: Float32Array): Float32Array {
  const target = out && out.length === vertexCount * 3 ? out : new Float32Array(vertexCount * 3);
  target.fill(clamp01(level));
  return target;
}
